const EventEmitter = require('eventemitter3');
const authBar = require('./authBar');
const embed = require('./embed');
const { NARCITY_CONFIG, MTLBLOG_CONFIG } = require('./brandConfigs');
const { createBrandAds } = require('./brandAdsFactory');
const { initClsLogger } = require('./lib/clsLogger');
const { initShiftComponentLogger } = require('./lib/shiftComponentLogger');
const identity = require('./lib/identity');
const privacyControls = require('./lib/identity/privacyControls');
const signIn = require('./lib/signIn');

const narcityAds = createBrandAds({
  brandConfig: NARCITY_CONFIG,
  logLabel: 'Narcity',
  articleTargeting: {
    includeCountry: true,
    includeCity: true,
    useFwjsonLang: true
  },
  homepageTargeting: {
    useFwjsonLang: true
  },
  resultsPageBaseUrl: 'http://www.narcity.com/search/?afs&partner_param=param'
});

const mtlblogAds = createBrandAds({
  brandConfig: MTLBLOG_CONFIG,
  logLabel: 'MTL Blog',
  articleTargeting: {
    includeCountry: false,
    includeCity: false,
    useFwjsonLang: false
  },
  homepageTargeting: {
    useFwjsonLang: false
  },
  resultsPageBaseUrl: 'http://www.mtlblog.com/search/?afs&partner_param=param'
});

const ACCESS_TOKEN_HEADER = 'x-milla-access-token';
const ACCESS_TTL_HEADER = 'x-milla-access-ttl';

const defaultConfig = {
  issuer: 'https://embed.getmilla.com',
  audience: 'https://api-v2.getmilla.com',
  allowedOrigins: ['https://www.narcity.com'],
  iframePath: '/frame',
  popupPath: '/popup',
  autoMountBar: true,
  embed: null,
  refreshTimeoutMs: 10000,
  refreshRetryDelayMs: 2500,
  // A refused or blocked /frame never posts READY (frame.js parent
  // allowlist); token consumers stop waiting for it after this long.
  iframeReadyTimeoutMs: 8000,
  // Display ads start without a profile (email, Pro) after this long rather
  // than wait on a slow or unavailable auth frame.
  adsAuthTimeoutMs: 2500,
  // Skip the refresh frame for readers without the server's session hint
  // cookie. null follows the build (MILLA_SDK_SESSION_HINT=enforce); see
  // plans/auth-embed-speed-and-upgrades.md for the 7-day rollout.
  sessionHint: null,
  loginMode: 'redirect',
  clsLogging: false,
  clsLogEachShift: true,
  clsLogMinShift: 0.001,
  shiftWatchLogging: false,
  shiftWatchSelectors: null,
  shiftWatchMinDelta: 40,
  // Unified Visitor Identity: `true` (all flags on), `false`, or
  // { bootstrap, aliasAdoption, publisherCapture, authHandoff, emailHandoff, debug }.
  // The brand auth host holds the kill switch; the SDK only asks.
  identity: true,
  // false stops the SDK's sign-in GA4 events (login_success, new_account).
  analytics: true
};

const state = {
  config: { ...defaultConfig },
  tokens: {
    accessToken: null,
    expiresAt: 0
  },
  pendingState: null,
  // { intent, context } for the sign-in in flight (login({ intent, context })).
  pendingIntent: null,
  loginInFlight: false,
  emitter: new EventEmitter(),
  iframe: null,
  iframeReady: false,
  iframeReadyPromise: null,
  resolveIframeReady: null,
  refreshPromise: null,
  refreshRetryTimeout: null,
  listenersAttached: false,
  channelInitialized: false,
  bc: null,
  popup: null,
  popupMonitor: null,
  // Track when we've confirmed user is logged out (no valid refresh token)
  // This allows us to skip unnecessary API calls and load ads faster
  knownLoggedOut: false,
  // Track if initial auth check has completed
  authCheckComplete: false,
  // Set when init skipped the refresh frame because the session hint is absent
  sessionHintSkipped: false,
  // In-flight /me request shared by the bar, ads and OneSignal: { token, promise }
  profileFetch: null
};

function clearRefreshRetryTimeout() {
  if (state.refreshRetryTimeout) {
    clearTimeout(state.refreshRetryTimeout);
    state.refreshRetryTimeout = null;
  }
}

function logInfo(...args) {
  if (typeof window !== 'undefined' && window.console?.info) {
    window.console.info('[milla-auth]', ...args);
  }
}

function logWarn(...args) {
  if (typeof window !== 'undefined' && window.console?.warn) {
    window.console.warn('[milla-auth]', ...args);
  }
}

function logError(...args) {
  if (typeof window !== 'undefined' && window.console?.error) {
    window.console.error('[milla-auth]', ...args);
  }
}

const hostOrigin = () => {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
};

const issuerOrigin = (cfg = state.config) => {
  try {
    return new URL(cfg.issuer).origin;
  } catch (err) {
    return cfg.issuer;
  }
};

const buildIssuerUrl = (path) => {
  const issuer = state.config.issuer || '';
  const base = issuer.replace(/\/$/, '');
  return `${base}${path}`;
};

const resolveLoginMode = (mode) => {
  if (mode === 'popup') return 'popup';
  if (mode === 'redirect') return 'redirect';
  return defaultConfig.loginMode;
};

function mergeConfig(userConfig = {}) {
  const loginMode = resolveLoginMode(userConfig.loginMode);
  state.config = {
    ...defaultConfig,
    ...userConfig,
    loginMode,
    allowedOrigins: userConfig.allowedOrigins || defaultConfig.allowedOrigins
  };
  return state.config;
}

function initCrossTabChannel() {
  if (state.channelInitialized || typeof window === 'undefined') return;
  state.channelInitialized = true;

  if (typeof window.BroadcastChannel !== 'undefined') {
    state.bc = new window.BroadcastChannel('milla-auth');
    state.bc.onmessage = handleCrossTabMessage;
  } else {
    window.addEventListener('storage', handleStorageEvent);
  }
  // Note: BroadcastChannel is automatically garbage collected on page unload,
  // no beforeunload listener needed (also blocked by some Permissions-Policy)
}

function ensureListeners() {
  if (state.listenersAttached || typeof window === 'undefined') return;
  state.listenersAttached = true;
  window.addEventListener('message', handleMessage);
  initCrossTabChannel();
}

function mountIframe() {
  if (typeof document === 'undefined') return null;
  if (state.iframeReadyPromise) return state.iframeReadyPromise;

  state.iframeReadyPromise = new Promise((resolve) => {
    state.resolveIframeReady = resolve;
  });

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.style.position = 'absolute';
  iframe.style.width = '1px';
  iframe.style.height = '1px';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.border = '0';
  iframe.src = `${state.config.issuer}${state.config.iframePath}?parent=${encodeURIComponent(hostOrigin())}`;

  document.body.appendChild(iframe);
  state.iframe = iframe;
  return state.iframeReadyPromise;
}

function randomState() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function login(options) {
  if (typeof window === 'undefined') return;
  identity.forgetAccountObservation();
  // Reset logged-out state since user is attempting to log in
  state.knownLoggedOut = false;
  mountIframe();
  const authState = randomState();
  state.pendingState = authState;
  state.pendingIntent = signIn.normalizeLoginOptions(options);
  signIn.rememberLoginContext(authState, state.pendingIntent);
  state.loginInFlight = true;
  try {
    const inviteActive = window.sessionStorage?.getItem?.('__milla_invite_active__') === '1';
    const inviteToken = window.sessionStorage?.getItem?.('__milla_invite_token__');
    const inviteEmail = window.sessionStorage?.getItem?.('__milla_invite_email__');
    if (inviteActive && (inviteToken || inviteEmail)) {
      window.sessionStorage?.setItem?.('__milla_invite_state__', authState);
    }
  } catch (_) {
    // ignore storage errors
  }
  if (state.config.loginMode === 'popup') {
    openPopupLogin(authState);
    return;
  }
  startRedirectLogin(authState);
}

function openPopupLogin(authState) {
  const width = 420;
  const height = 620;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;
  const popupParams = new URLSearchParams({
    parent: hostOrigin(),
    state: authState
  });
  try {
    const inviteActive = window.sessionStorage?.getItem?.('__milla_invite_active__') === '1';
    const inviteState = window.sessionStorage?.getItem?.('__milla_invite_state__');
    const inviteToken = window.sessionStorage?.getItem?.('__milla_invite_token__');
    if (inviteActive && inviteToken && inviteState === authState) {
      popupParams.set('invitation_token', inviteToken);
    }
    const inviteEmail = window.sessionStorage?.getItem?.('__milla_invite_email__');
    if (inviteActive && inviteEmail && inviteState === authState) {
      popupParams.set('login_hint', inviteEmail);
    }
  } catch (_) {
    // ignore storage errors
  }
  const popupUrl = `${state.config.issuer}${state.config.popupPath}?${popupParams.toString()}`;
  logInfo('Opening login popup', { issuer: state.config.issuer, popupUrl });
  const popupRef = window.open(popupUrl, 'milla-auth', features);
  state.popup = popupRef || null;
  if (state.popup) {
    state.emitter.emit('login:opened');
    monitorPopup();
    return;
  }

  logWarn('Popup blocked or unavailable. Falling back to redirect login.');
  startRedirectLogin(authState);
}

function startRedirectLogin(authState) {
  let returnTo = typeof window !== 'undefined' ? window.location.href : null;
  if (returnTo) {
    try {
      const url = new URL(returnTo);
      const path = url.pathname.replace(/\/+$/, '') || '/';
      if (path === '/invite') {
        url.pathname = '/account';
        url.searchParams.set('tab', 'subscriptions');
        url.searchParams.delete('invitation_token');
        url.searchParams.delete('invitationToken');
        url.searchParams.delete('token');
        url.searchParams.delete('email');
        returnTo = url.toString();
      }
    } catch (_) {
      // ignore URL parsing errors
    }
  }
  try {
    if (returnTo) {
      window.sessionStorage?.setItem?.('__milla_return_to__', returnTo);
    }
  } catch (_) {
    // ignore storage errors
  }
  logInfo('Starting redirect login', { issuer: state.config.issuer, returnTo });
  requestAuthorizationUrl(authState, { returnTo })
    .then((url) => {
      if (!url) {
        throw new Error('authorization_url_missing');
      }
      logInfo('Redirecting to authorization URL');
      state.emitter.emit('login:opened');
      window.location.assign(url);
    })
    .catch((err) => {
      logError('Redirect login failed', err);
      state.pendingState = null;
      state.loginInFlight = false;
      const error = new Error(err?.message || 'redirect_failed');
      error.code = err?.code || err?.error || 'redirect_failed';
      state.emitter.emit('login:failed');
      state.emitter.emit('token:error', error);
    });
}

async function logout() {
  state.tokens.accessToken = null;
  state.tokens.expiresAt = 0;
  sendMessageToIframe('MILLA_REFRESH_CLEAR');
  state.emitter.emit('logout');
  identity.forgetAccountObservation();

  const parentOrigin = hostOrigin();
  const returnTo = typeof window !== 'undefined' ? window.location.href : undefined;
  broadcast({ type: 'MILLA_LOGOUT_BROADCAST' });

  if (typeof window === 'undefined') {
    return;
  }

  try {
    const logoutUrl = new URL(`${state.config.issuer}/api/auth/logout`);
    if (parentOrigin) {
      logoutUrl.searchParams.set('parentOrigin', parentOrigin);
    }
    if (returnTo) {
      logoutUrl.searchParams.set('returnTo', returnTo);
    }
    window.location.assign(logoutUrl.toString());
  } catch (err) {
    // ignore URL issues; logout already cleared local state
  }
}

function handleMessage(event) {
  if (!event?.origin) return;

  const trustedOrigin = issuerOrigin();
  if (event.origin !== trustedOrigin) {
    return;
  }

  const { type, payload } = event.data || {};
  switch (type) {
    case 'MILLA_IFRAME_READY':
      state.iframeReady = true;
      if (state.resolveIframeReady) {
        state.resolveIframeReady();
        state.resolveIframeReady = null;
      }
      break;
    case 'MILLA_POPUP_READY':
      if (state.config.loginMode === 'popup') {
        handlePopupReady(payload, event);
      }
      break;
    case 'MILLA_POPUP_ERROR':
      if (state.config.loginMode === 'popup') {
        handlePopupError(payload);
      }
      break;
    case 'MILLA_AUTH_SUCCESS':
      if (state.config.loginMode === 'popup') {
        handleAuthSuccess(payload);
      }
      break;
    case 'MILLA_AUTH_TOKEN':
      applyTokens(payload);
      break;
    case 'MILLA_REFRESH_ERROR':
      state.emitter.emit('token:error', payload);
      break;
    case 'MILLA_REFRESH_RATE_LIMITED':
      handleRefreshRateLimited(payload);
      break;
    case 'MILLA_AUTH_TOKEN_REQUEST':
      // Respond to embed iframes requesting auth token
      handleAuthTokenRequest(event);
      break;
    default:
      break;
  }
}

function handleRefreshRateLimited(payload = {}) {
  if (!state.refreshPromise) {
    return;
  }
  const retryPayload = typeof payload.retryInMs === 'number' && payload.retryInMs > 0 ? payload.retryInMs : null;
  const retryInMs = retryPayload || state.config.refreshRetryDelayMs;
  clearRefreshRetryTimeout();
  logWarn('Refresh rate limited. Retrying soon.', { retryInMs });
  state.refreshRetryTimeout = setTimeout(() => {
    state.refreshRetryTimeout = null;
    if (!state.refreshPromise) {
      return;
    }
    sendMessageToIframe('MILLA_TOKEN_REFRESH_REQUEST');
  }, retryInMs);
}

function handleAuthTokenRequest(event) {
  // Respond to embed iframes requesting auth token for authenticated checkout
  const source = event.source;
  const origin = event.origin;
  if (!source) return;

  const response = {
    type: 'MILLA_AUTH_TOKEN_RESPONSE',
    payload: null
  };

  // If user is authenticated, include the access token
  if (state.tokens.accessToken && state.tokens.expiresAt > Date.now()) {
    response.payload = {
      accessToken: state.tokens.accessToken
    };
    logInfo('Providing auth token to embed iframe');
  }

  try {
    source.postMessage(response, origin);
  } catch (err) {
    logWarn('Failed to send auth token response to iframe', err);
  }
}

function applyTokens(payload = {}) {
  if (!payload.accessToken || !payload.ttl) {
    return;
  }
  clearRefreshRetryTimeout();
  state.tokens.accessToken = payload.accessToken;
  state.tokens.expiresAt = Date.now() + payload.ttl;
  // User is authenticated - reset logged out flag
  const wasLoggedOut = state.knownLoggedOut;
  state.knownLoggedOut = false;
  const wasAuthCheckComplete = state.authCheckComplete;
  state.authCheckComplete = true;
  logInfo('Access token received', { expiresAt: state.tokens.expiresAt });
  // Emit auth:ready if this is the first successful auth check
  if (!wasAuthCheckComplete || wasLoggedOut) {
    state.emitter.emit('auth:ready', { authenticated: true });
  }
  state.emitter.emit('token', state.tokens.accessToken);
  // Flow G: a fresh session on a DURABLE identity attaches this browser's
  // visitor alias to the account (fail-open, once per session).
  Promise.resolve(identity.observeAuthenticatedSession()).catch(() => {});
  broadcast({
    type: 'MILLA_TOKEN_BROADCAST',
    payload: { accessToken: payload.accessToken, ttl: payload.ttl }
  });
}

function tryConsumeRedirectHash() {
  if (typeof window === 'undefined') {
    return;
  }
  const hash = window.location?.hash || '';
  if (!hash || hash.length < 2) {
    return;
  }
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const raw = params.get('milla_auth');
  if (!raw) {
    return;
  }
  try {
    const decoded = decodeURIComponent(raw);
    const data = JSON.parse(decoded);
    const completedSignIn = data?.signIn;
    const accessToken = data?.accessToken;
    const expiresInSeconds = data?.expiresIn;
    const ttlMs =
      typeof data?.ttl === 'number' && data.ttl > 0
        ? data.ttl
        : Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
            ? Math.round(expiresInSeconds * 1000)
            : null;
    if (typeof accessToken === 'string' && accessToken && ttlMs && ttlMs > 0) {
      applyTokens({ accessToken, ttl: ttlMs });
      signIn.reportSignIn(completedSignIn, { emitter: state.emitter, analytics: state.config.analytics });
    }
  } catch (err) {
    // ignore malformed hash payloads
  }

  try {
    const nextUrl = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState(null, '', nextUrl);
  } catch (_) {
    // ignore history issues
  }
}

function applyTokensFromResponse(response) {
  if (!response || typeof response.headers?.get !== 'function') {
    return;
  }
  const nextToken = response.headers.get(ACCESS_TOKEN_HEADER);
  if (!nextToken) {
    return;
  }
  const ttlHeader = response.headers.get(ACCESS_TTL_HEADER);
  const ttlMs = Number(ttlHeader);
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    logWarn('Received refreshed access token without usable TTL.');
    return;
  }
  applyTokens({ accessToken: nextToken, ttl: ttlMs });
}

function handlePopupReady(payload = {}, event) {
  const readyState = payload?.state;
  logInfo('Popup ready', { state: readyState });
  if (!state.pendingState) {
    state.pendingState = readyState || state.pendingState;
  }
  if (readyState && state.pendingState && readyState !== state.pendingState) {
    handlePopupError({ code: 'state_mismatch', message: 'Popup state does not match request.' });
    return;
  }
  if (!state.pendingState) {
    handlePopupError({ code: 'state_missing', message: 'Missing login state.' });
    return;
  }
  if (!state.popup || state.popup.closed) {
    state.popup = event?.source || state.popup;
  }
  logInfo('Requesting authorization URL', { state: state.pendingState });
  requestAuthorizationUrl(state.pendingState)
    .then((url) => {
      if (!url) {
        throw new Error('authorization_url_missing');
      }
      logInfo('Authorization URL received');
      sendMessageToPopup('MILLA_OPEN_AUTHKIT', { url });
    })
    .catch((err) => {
      handlePopupError({ code: 'authorization_failed', message: err.message });
    });
}

function handlePopupError(payload = {}) {
  logError('Popup error', payload);
  closePopup();
  const error = new Error(payload.message || 'Authentication error');
  error.code = payload.code || 'popup_error';
  state.emitter.emit('login:failed');
  state.emitter.emit('token:error', error);
}

function handleAuthSuccess(payload = {}) {
  logInfo('Authentication completed', { state: payload.state });
  // Popup messages for an older sign-in are ignored for reporting.
  if (payload.state && payload.state === state.pendingState) {
    signIn.reportSignIn(payload.signIn, { emitter: state.emitter, analytics: state.config.analytics });
  }
  state.pendingState = null;
  state.pendingIntent = null;
  state.loginInFlight = false;
  closePopup();
  triggerPostPopupRefresh();
}

async function requestAuthorizationUrl(authState, options = {}) {
  if (!authState) {
    throw new Error('missing_state');
  }

  logInfo('Calling /api/auth/authorize');
  const payload = {
    state: authState,
    parentOrigin: hostOrigin()
  };
  // Unified Visitor Identity (Flow E): bind this login to the visitor's
  // WORKOS_LINK handoff. Opaque token in the JSON body only; null keeps the
  // flow unlinked and never blocks the login.
  const identityHandoff = await identity.requestAuthHandoff(authState);
  if (identityHandoff) payload.identityHandoff = identityHandoff;
  // Why this sign-in started; body only, validated again by the server.
  if (state.pendingIntent && authState === state.pendingState) {
    payload.intent = state.pendingIntent.intent;
    if (state.pendingIntent.context) payload.context = state.pendingIntent.context;
    if (state.pendingIntent.provider) payload.provider = state.pendingIntent.provider;
    if (state.pendingIntent.screen) payload.screen = state.pendingIntent.screen;
  }
  if (typeof options.returnTo === 'string' && options.returnTo.length) {
    payload.returnTo = options.returnTo;
  } else if (typeof window !== 'undefined' && window.location?.href) {
    payload.returnTo = window.location.href;
  }
  try {
    const inviteActive = window.sessionStorage?.getItem?.('__milla_invite_active__') === '1';
    const inviteState = window.sessionStorage?.getItem?.('__milla_invite_state__');
    const inviteToken = window.sessionStorage?.getItem?.('__milla_invite_token__');
    if (inviteActive && inviteToken && inviteState === authState) {
      payload.invitationToken = inviteToken;
    }
    const inviteEmail = window.sessionStorage?.getItem?.('__milla_invite_email__');
    if (inviteActive && inviteEmail && inviteState === authState) {
      payload.loginHint = inviteEmail;
    }
  } catch (_) {
    // ignore storage errors
  }
  const response = await fetch(`${state.config.issuer}/api/auth/authorize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload)
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    logError('Authorization request failed', data);
    throw new Error(data?.error || 'authorize_failed');
  }

  if (!data.url) {
    logError('Authorization URL missing in response');
    throw new Error('authorization_url_missing');
  }

  return data.url;
}

function sendMessageToIframe(type, payload) {
  if (!state.iframe || !state.iframe.contentWindow) return;
  const targetOrigin = issuerOrigin();
  state.iframe.contentWindow.postMessage(
    {
      type,
      payload
    },
    targetOrigin
  );
}

function sendMessageToPopup(type, payload) {
  if (!state.popup || state.popup.closed) return;
  const targetOrigin = issuerOrigin();
  try {
    state.popup.postMessage(
      {
        type,
        payload
      },
      targetOrigin
    );
  } catch (_) {
    // swallow cross-origin errors; popup may have navigated away already
  }
}

function broadcast(message) {
  if (state.bc) {
    state.bc.postMessage(message);
    return;
  }

  try {
    const payload = { ...message, timestamp: Date.now() };
    window.localStorage.setItem('__milla_broadcast__', JSON.stringify(payload));
    window.localStorage.removeItem('__milla_broadcast__');
  } catch (_) {
    // Ignore storage issues
  }
}

function closePopup() {
  if (state.popup && !state.popup.closed) {
    try {
      state.popup.close();
    } catch (_) {
      // ignore
    }
  }
  state.popup = null;
  if (state.popupMonitor) {
    clearInterval(state.popupMonitor);
    state.popupMonitor = null;
  }
}

function monitorPopup() {
  if (typeof window === 'undefined') return;
  if (state.popupMonitor) {
    clearInterval(state.popupMonitor);
  }
  state.popupMonitor = window.setInterval(() => {
  if (!state.popup || state.popup.closed) {
      clearInterval(state.popupMonitor);
      state.popupMonitor = null;
      logInfo('Popup closed');
      closePopup();
      if (state.pendingState) {
        const hadSession = Boolean(state.tokens.accessToken);
        const awaitingLogin = state.loginInFlight;
        state.pendingState = null;
        state.loginInFlight = false;
        if (hadSession || awaitingLogin) {
          logInfo('Popup closed; attempting silent refresh.');
          triggerPostPopupRefresh();
        } else {
          logWarn('Popup closed before completion with no existing session.');
          state.emitter.emit('token:error', new Error('popup_closed'));
        }
      }
    }
  }, 500);
}

function triggerPostPopupRefresh() {
  try {
    requestAccessToken().catch((err) => logWarn('Post-popup refresh failed', err));
  } catch (err) {
    logWarn('Unable to trigger post-popup refresh', err);
  }
}

function handleStorageEvent(event) {
  if (event.key !== '__milla_broadcast__' || !event.newValue) {
    return;
  }
  try {
    const data = JSON.parse(event.newValue);
    handleCrossTabMessage({ data });
  } catch (_) {
    // ignore malformed payloads
  }
}

function handleCrossTabMessage(event) {
  const { type, payload } = event.data || {};
  if (!type) return;
  if (type === 'MILLA_LOGOUT_BROADCAST') {
    state.tokens.accessToken = null;
    state.tokens.expiresAt = 0;
    state.emitter.emit('logout');
    identity.forgetAccountObservation();
  }
  if (type === 'MILLA_TOKEN_BROADCAST' && payload?.accessToken && payload?.ttl) {
    state.tokens.accessToken = payload.accessToken;
    state.tokens.expiresAt = Date.now() + payload.ttl;
    state.emitter.emit('token', state.tokens.accessToken);
    Promise.resolve(identity.observeAuthenticatedSession()).catch(() => {});
  }
  if (type === 'MILLA_PROFILE_UPDATED') {
    // Another tab updated profile, refresh the bar
    authBar.refreshProfile();
  }
}

// The frame never posts READY when it refuses this parent (frame.js parent
// allowlist) or is blocked. Bound the wait so every token consumer settles;
// a frame that becomes ready later still serves the next request.
function waitForIframeReady() {
  if (state.iframeReady) return Promise.resolve();
  const timeoutMs = state.config.iframeReadyTimeoutMs > 0 ? state.config.iframeReadyTimeoutMs : defaultConfig.iframeReadyTimeoutMs;
  let timer;
  return Promise.race([
    state.iframeReadyPromise,
    new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('iframe_unavailable')), timeoutMs); })
  ]).finally(() => clearTimeout(timer));
}

async function requestAccessToken() {
  if (state.tokens.accessToken && state.tokens.expiresAt > Date.now() + 2000) {
    return state.tokens.accessToken;
  }
  // init found no session hint: nothing to refresh until login() clears it.
  if (state.sessionHintSkipped && state.knownLoggedOut) {
    return null;
  }

  mountIframe();
  if (!state.iframeReadyPromise) {
    state.iframeReadyPromise = Promise.resolve();
  }
  await waitForIframeReady();

  if (!state.refreshPromise) {
    state.refreshPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('refresh_timeout'));
      }, state.config.refreshTimeoutMs);

      const cleanup = () => {
        clearTimeout(timeout);
        state.emitter.off('token', handleToken);
        state.emitter.off('token:error', handleError);
        state.refreshPromise = null;
        clearRefreshRetryTimeout();
      };

      const handleToken = (token) => {
        cleanup();
        resolve(token);
      };

      const handleError = (err) => {
        const code =
          typeof err === 'string'
            ? err
            : err?.code || err?.error || err?.message || null;
        if (code === 'refresh_missing' || code === 'refresh_invalid') {
          state.tokens.accessToken = null;
          state.tokens.expiresAt = 0;
          // Mark user as confirmed logged out - skip future API calls
          state.knownLoggedOut = true;
          state.authCheckComplete = true;
          cleanup();
          // Signal to consumers that there is no valid session.
          // Emit auth:ready first so consumers know auth state is determined
          state.emitter.emit('auth:ready', { authenticated: false });
          state.emitter.emit('logout');
          identity.forgetAccountObservation();
          resolve(null);
          return;
        }
        // Session ended means the user must re-authenticate - trigger full logout flow
        if (code === 'session_ended' || code === 'SESSION_ENDED') {
          logWarn('Session has ended. User must sign in again.');
          state.tokens.accessToken = null;
          state.tokens.expiresAt = 0;
          cleanup();
          // Trigger logout which will redirect user to login
          logout();
          resolve(null);
          return;
        }
        cleanup();
        reject(err || new Error('refresh_failed'));
      };

      state.emitter.once('token', handleToken);
      state.emitter.once('token:error', handleError);
      sendMessageToIframe('MILLA_TOKEN_REFRESH_REQUEST');
    });
  }

  return state.refreshPromise;
}

const PROFILE_PATH = '/me';

function isIssuerProfileRequest(input, init) {
  const method = String(init.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
  if (method !== 'GET' || typeof window === 'undefined') return false;
  try {
    const url = new URL(typeof input === 'string' ? input : input.url, window.location.href);
    return url.origin === issuerOrigin() && url.pathname === PROFILE_PATH;
  } catch {
    return false;
  }
}

// The bar, ads and OneSignal each read /me at startup with the same bearer.
// Share one request while it is in flight; each caller gets its own copy.
function sharedProfileFetch(input, init, token) {
  const inflight = state.profileFetch;
  if (inflight && inflight.token === token) {
    return inflight.promise.then((response) => response.clone());
  }
  const promise = fetch(input, init).then((response) => {
    applyTokensFromResponse(response);
    return response;
  });
  state.profileFetch = { token, promise };
  const settle = () => {
    if (state.profileFetch?.promise === promise) state.profileFetch = null;
  };
  promise.then(settle, settle);
  return promise.then((response) => response.clone());
}

async function authFetch(input, init = {}) {
  // If we've confirmed user is logged out, skip the refresh attempt entirely
  // This reduces failed API calls and speeds up ad loading for logged-out users
  let token = null;
  if (!state.knownLoggedOut) {
    token = await requestAccessToken();
  }
  const profileRequest = isIssuerProfileRequest(input, init);
  // /me without a bearer always answers 401 missing_token. A known signed-out
  // reader gets that answer locally (anonymous /me was ~9% of auth-host
  // traffic). Other requests still go out: MillaAuth.fetch also serves
  // optional-auth endpoints.
  if (profileRequest && !token && state.knownLoggedOut) {
    return new Response(JSON.stringify({ error: 'missing_token' }), {
      status: 401, headers: { 'Content-Type': 'application/json' }
    });
  }
  const headers = new Headers(init.headers || {});
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  if (profileRequest && token) {
    return sharedProfileFetch(input, { ...init, headers }, token);
  }
  const response = await fetch(input, { ...init, headers });
  applyTokensFromResponse(response);
  return response;
}

/**
 * Helper to throw early if user is known to be logged out
 * Prevents unnecessary API calls that will definitely fail
 */
function requireAuth(operation) {
  if (state.knownLoggedOut) {
    const error = new Error('User is not authenticated');
    error.code = 'missing_token';
    throw error;
  }
}

/**
 * One event ID per logical interaction so the server can dedupe retries of
 * the same action end-to-end (engagement event store idempotency).
 */
function generateInteractionEventId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return 'sdk_' + crypto.randomUUID().replace(/-/g, '');
    }
  } catch (error) {
    // Fall through to the non-crypto fallback below.
  }
  const random = () => Math.random().toString(36).slice(2, 10);
  return 'sdk_' + Date.now().toString(36) + random() + random();
}

async function getHistory() {
  requireAuth('getHistory');
  const url = buildIssuerUrl('/api/portal/history');
  const response = await authFetch(url, {
    method: 'GET',
    credentials: 'include',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'history_fetch_failed');
    error.code = data?.error || 'history_fetch_failed';
    throw error;
  }
  return Array.isArray(data?.items) ? data.items : [];
}

async function addToHistory(article) {
  requireAuth('addToHistory');
  const url = buildIssuerUrl('/api/portal/history');
  const response = await authFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ article, eventId: generateInteractionEventId() }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'history_add_failed');
    error.code = data?.error || 'history_add_failed';
    throw error;
  }
  return {
    success: data?.success !== false,
    message: data?.message || 'Article added to reading history',
  };
}

async function clearHistory() {
  requireAuth('clearHistory');
  const url = buildIssuerUrl('/api/portal/history');
  const response = await authFetch(url, {
    method: 'DELETE',
    credentials: 'include',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'history_clear_failed');
    error.code = data?.error || 'history_clear_failed';
    throw error;
  }
  return {
    success: data?.success !== false,
    message: data?.message || 'All reading history removed',
  };
}

async function getFavourites() {
  requireAuth('getFavourites');
  const url = buildIssuerUrl('/api/portal/favourites');
  const response = await authFetch(url, {
    method: 'GET',
    credentials: 'include',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'favourites_fetch_failed');
    error.code = data?.error || 'favourites_fetch_failed';
    throw error;
  }
  return Array.isArray(data?.items) ? data.items : [];
}

async function addFavourite(article) {
  requireAuth('addFavourite');
  const url = buildIssuerUrl('/api/portal/favourites');
  const response = await authFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ article, eventId: generateInteractionEventId() }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'favourite_add_failed');
    error.code = data?.error || 'favourite_add_failed';
    throw error;
  }
  return {
    success: data?.success !== false,
    message: data?.message || 'Article added to favourites',
  };
}

async function removeFavourite(articleId) {
  requireAuth('removeFavourite');
  // Use POST to /remove endpoint to avoid CORS issues with DELETE method
  const url = buildIssuerUrl('/api/portal/favourites/remove');
  const response = await authFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ articleId, eventId: generateInteractionEventId() }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'favourite_remove_failed');
    error.code = data?.error || 'favourite_remove_failed';
    throw error;
  }
  return {
    success: data?.success !== false,
    message: data?.message || 'Article removed from favourites',
  };
}

async function isFavourite(articleId) {
  requireAuth('isFavourite');
  const url = buildIssuerUrl(`/api/portal/favourites/${encodeURIComponent(articleId)}`);
  const response = await authFetch(url, {
    method: 'GET',
    credentials: 'include',
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'favourite_check_failed');
    error.code = data?.error || 'favourite_check_failed';
    throw error;
  }
  return { isSaved: !!data?.isSaved };
}

async function summarize(text) {
  requireAuth('summarize');
  const url = buildIssuerUrl('/api/portal/summarize');
  const response = await authFetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text }),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'summarize_failed');
    error.code = data?.error || 'summarize_failed';
    throw error;
  }
  return data?.summaryText || null;
}

/**
 * Check if user is currently authenticated (has valid token)
 * @returns {boolean}
 */
function isAuthenticated() {
  return Boolean(state.tokens.accessToken && state.tokens.expiresAt > Date.now());
}

// Flow G: the identity module reads the current session through this getter
// (never stores the token itself) when it observes the signed-in account.
function currentAccessToken() {
  return state.tokens.accessToken && state.tokens.expiresAt > Date.now() + 2000 ? state.tokens.accessToken : null;
}

/**
 * Check if we've confirmed user is logged out (no valid refresh token)
 * When true, API calls requiring auth will be skipped to reduce failed requests
 * @returns {boolean}
 */
function isKnownLoggedOut() {
  return state.knownLoggedOut;
}

/**
 * Get the current auth state
 * @returns {{ authenticated: boolean, knownLoggedOut: boolean, authCheckComplete: boolean }}
 */
function getAuthState() {
  return {
    authenticated: isAuthenticated(),
    knownLoggedOut: state.knownLoggedOut,
    authCheckComplete: state.authCheckComplete
  };
}

function init(userConfig) {
  const cfg = mergeConfig(userConfig);
  initClsLogger({
    enabled: cfg.clsLogging === true,
    logEachShift: cfg.clsLogEachShift !== false,
    minShiftValue: cfg.clsLogMinShift
  });
  initShiftComponentLogger({
    enabled: cfg.shiftWatchLogging === true,
    selectors: Array.isArray(cfg.shiftWatchSelectors) ? cfg.shiftWatchSelectors : null,
    minDelta: cfg.shiftWatchMinDelta
  });
  ensureListeners();
  // Unified Visitor Identity (D19): one shared bootstrap per page context,
  // started before any product module; fail-open for everything else.
  identity.configure(cfg, { emitter: state.emitter, getAccessToken: currentAccessToken });
  if (typeof window !== 'undefined') {
    privacyControls.mount(api, cfg);
    identity.ensureIdentityBootstrap();
    // Ad scripts first: GPT and Prebid (and so the CMP) start downloading
    // before the auth bar, comments and app prompt set up.
    if (cfg.embed) embed.preloadAdScripts(api, cfg);
    identity.installEmailHandoffBridge();
    if (!skipRefreshFrame(cfg)) mountIframe();
    tryConsumeRedirectHash();
    if (cfg.autoMountBar) {
      authBar.mount(api);
    }
    // Legacy publisher snippets omit `embed`. Comments and the homepage Shows
    // widget still initialize, with no CMS change or duplicate legacy helpers.
    if (cfg.embed !== false) {
      const embedOptions = cfg.embed && typeof cfg.embed === 'object' ? cfg.embed : {};
      require('./lib/comments/loader').setupComments(api, cfg, embedOptions,
        (...args) => state.emitter.emit(...args), currentAccessToken, requestAccessToken);
      if (embedOptions.shows !== false) {
        require('./lib/shows/homepage').setupHomepageShows(api, { brand: embed.resolveBrand(embedOptions.brand || cfg.embed, cfg.issuer) });
      }
      if (embedOptions.appPrompt !== false) {
        require('./lib/appPrompt').setupAppPrompt({ brand: embed.resolveBrand(embedOptions.brand || cfg.embed, cfg.issuer),
          issuer: cfg.issuer, isArticle: embed.isArticlePage });
      }
      // Dynamic login wall (plans/dynamic-login-wall.md): article pages only,
      // through the window.millaPage contract.
      if (embedOptions.loginWall !== false) {
        require('./lib/loginWall').setupLoginWall({ api, brand: embed.resolveBrand(embedOptions.brand || cfg.embed, cfg.issuer),
          issuer: cfg.issuer, isArticle: embed.isArticlePage });
      }
    }
    // Publisher embed layer (history tracking, MillaInteractions, OneSignal,
    // ad helpers) — opt in with `embed: true | 'narcity' | 'mtlblog' | {...}`.
    if (cfg.embed) {
      embed.setup(api, cfg);
    }
    // www.mtlblog.com only: web vitals for the RebelMouse/Expo comparison.
    require('./lib/frontendMeasurement').setupFrontendVitals({ issuer: cfg.issuer });
  }
  return cfg;
}

// Matches the server's config.security.sessionHintCookieName.
const SESSION_HINT_COOKIE = 'milla_session';
// eslint-disable-next-line no-undef -- esbuild define (portal/sdk/scripts/build.js)
const BUILD_SESSION_HINT = typeof __MILLA_SESSION_HINT__ !== 'undefined' && __MILLA_SESSION_HINT__ === true;

// The server sets the hint with milla_refresh, on the brand's registrable
// domain (server utils/refreshCookie.js). On a page that can read it, its
// absence means there is no session to refresh: answer signed-out locally and
// mount no refresh frame. Off unless the build or init enables it.
function skipRefreshFrame(cfg) {
  const enforced = cfg.sessionHint === true || (cfg.sessionHint !== false && BUILD_SESSION_HINT);
  if (!enforced || typeof document === 'undefined' || typeof window === 'undefined') return false;
  if (isAuthenticated()) return false;
  let pageHost;
  let issuerHost;
  try {
    pageHost = window.location.hostname;
    issuerHost = new URL(cfg.issuer).hostname;
  } catch {
    return false;
  }
  const site = (host) => host.split('.').slice(-2).join('.');
  if (!issuerHost.includes('.') || site(pageHost) !== site(issuerHost)) return false;
  if (new RegExp(`(?:^|;\\s*)${SESSION_HINT_COOKIE}=1(?:;|$)`).test(document.cookie || '')) return false;
  // A login redirect back to this page carries its tokens in the hash.
  if (/(?:^#|[#&])milla_auth=/.test(window.location.hash || '')) return false;
  state.knownLoggedOut = true;
  state.authCheckComplete = true;
  state.sessionHintSkipped = true;
  Promise.resolve().then(() => state.emitter.emit('auth:ready', { authenticated: false }));
  return true;
}

function bootstrapIdentityForAds() {
  try {
    if (!identity._state.issuer) identity.configure(state.config, { emitter: state.emitter, getAccessToken: currentAccessToken });
    privacyControls.mount(api, state.config);
    identity.ensureIdentityBootstrap();
  } catch (_) {
    // identity is fail-open for ads
  }
}

const api = {
  init,
  login,
  logout,
  fetch: authFetch,
  on: (...args) => state.emitter.on(...args),
  once: (...args) => state.emitter.once(...args),
  off: (...args) => state.emitter.off(...args),
  getConfig: () => state.config,
  createShowsPlayer: (options) => { if (!options?.preview) bootstrapIdentityForAds(); return require('./lib/shows/player').createShowsPlayer(options); },
  mountShows: (options) => require('./lib/shows/loader').mountShows(options, state.config || {}),
  // Auth state methods - useful for skipping unnecessary API calls
  isAuthenticated,
  isKnownLoggedOut,
  getAuthState,
  mountBar: () => authBar.mount(api),
  unmountBar: () => authBar.unmount(),
  refreshProfile: () => {
    authBar.refreshProfile();
    // Broadcast to other tabs on same origin
    broadcast({ type: 'MILLA_PROFILE_UPDATED' });
  },
  // D19: every advertising entry point bootstraps identity idempotently, so an
  // ads-only page is covered; ads never wait on the identity response.
  initNarcityAds: (config) => { bootstrapIdentityForAds(); return narcityAds.initAds(config, state.emitter); },
  preloadNarcityAds: (config) => { bootstrapIdentityForAds(); return narcityAds.preloadAds(config); },
  initMtlblogAds: (config) => { bootstrapIdentityForAds(); return mtlblogAds.initAds(config, state.emitter); },
  preloadMtlblogAds: (config) => { bootstrapIdentityForAds(); return mtlblogAds.preloadAds(config); },
  identity: {
    requestEmailHandoff: (input) => identity.requestEmailHandoff(input),
    openPrivacyChoices: () => privacyControls.open(),
    ensureBootstrap: () => identity.ensureIdentityBootstrap(),
    getStatus: () => identity.getIdentityStatus(),
    optOut: () => identity.optOut(),
    optIn: () => identity.optIn(),
    isOptedOut: () => identity.isOptedOut(),
    // Advertising identifiers (plans/ads-identity-unification.md): status
    // only (state / policy / source — never the id) and the per-purpose
    // first-party opt-out.
    ads: {
      getStatus: () => identity.getAdsStatus(),
      optOut: () => identity.adsOptOut(),
      optIn: () => identity.adsOptIn(),
      isOptedOut: () => identity.isAdsOptedOut()
    },
    // Linkinbio capture (registry 2026-09-04.1) and Flow G observation.
    capture: (type, object) => identity.capture(type, object),
    observeAccount: () => identity.observeAuthenticatedSession()
  },
  getHistory,
  addToHistory,
  clearHistory,
  getFavourites,
  addFavourite,
  removeFavourite,
  isFavourite,
  summarize
};

if (typeof window !== 'undefined') {
  window.MillaAuth = api;
}

module.exports = api;
